import {
	BrowserMatcher,
	DeviceMatcher,
	DomainMatcher,
	GeoData,
	InstantConfigInterpreter,
	InstantConfigLoader,
	InstantConfigLoaderParams,
	InstantConfigOverrider,
	InstantConfigValue,
	RegionMatcher,
} from '@wikia/instant-config-loader';
import { utils } from '../../index';
import { Dictionary } from '../../models';
import { getContinentCode, getCountryCode, getRegionCode } from '../../utils';

const logGroup = 'instant-config-service';

export interface InstantConfigServiceInterface {
	get<T extends InstantConfigValue>(key: string, defaultValue?: T): T;
}

export class InstantConfigService implements InstantConfigServiceInterface {
	private interpreter!: InstantConfigInterpreter;
	private repository: Dictionary<InstantConfigValue> = {};

	constructor(private readonly params: InstantConfigLoaderParams) {}

	async init(globals: Dictionary = {}): Promise<InstantConfigService> {
		const geo: GeoData = {
			country: getCountryCode(),
			continent: getContinentCode(),
			region: getRegionCode(),
		};
		const instantConfigLoader = new InstantConfigLoader(this.params);
		const instantConfigInterpreter = new InstantConfigInterpreter(
			new BrowserMatcher(utils.client.getBrowser()),
			new DeviceMatcher(utils.client.getDeviceType() as unknown as string),
			new DomainMatcher(),
			new RegionMatcher(geo),
		);

		this.interpreter = await instantConfigLoader
			.getConfig()
			.then((config) =>
				new InstantConfigOverrider().override(utils.queryString.getURLSearchParams(), config),
			)
			.then((config) => instantConfigInterpreter.init(config, globals));
		this.repository = this.interpreter.getValues();

		utils.logger(logGroup, 'instantiated with', this.repository);

		return this;
	}

	get<T extends InstantConfigValue>(key: string, defaultValue?: T): T {
		return (this.repository?.[key] as T) ?? defaultValue;
	}

	getActiveKeyValues(): string[] {
		return this.interpreter.getActiveKeyValues();
	}

	getActiveLabradorKeyValues(): string[] {
		return this.interpreter.getActiveLabradorKeyValues();
	}
}
